import React, { useMemo, useState } from 'react';
import { Plus, Send, Shuffle, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { DigitBoxInput } from '../ui/DigitBoxInput';
import { confirmPurchase, showError } from '../../utils/swal';
import { formatMoney } from '../../utils/formatMoney';
import {
  LAO_BET_TYPE_HINTS,
  LAO_BET_TYPE_LABELS,
  LAO_DEFAULT_BET_TYPES,
  formatLaoPayout,
  laoDigitLength,
  randomLaoNumber,
} from '../../utils/laoUtils';

const QUICK_AMOUNTS = [1, 5, 10, 50, 100];
const MIN_BET = 1;

export function LaoBetDesk({
  config,
  bettingOpen,
  balance,
  onPlaceBets,
  isSubmitting,
}) {
  const betTypes = config?.betTypes?.length
    ? config.betTypes
    : LAO_DEFAULT_BET_TYPES;
  const [betType, setBetType] = useState('three_top');
  const [number, setNumber] = useState('');
  const [amount, setAmount] = useState('1');
  const [slip, setSlip] = useState([]);

  const active = useMemo(
    () => betTypes.find((item) => item.id === betType) || betTypes[0],
    [betTypes, betType],
  );
  const digitLength = active?.digits || laoDigitLength(betType);
  const multiplier = active?.multiplier || 0;
  const numericAmount = Number(amount) || 0;
  const isComplete = number.length === digitLength;
  const slipTotal = slip.reduce((sum, line) => sum + line.amount, 0);
  const hasEnough = balance >= slipTotal && (slip.length > 0 || balance >= numericAmount);

  const handleTabChange = (nextType) => {
    setBetType(nextType);
    setNumber('');
  };

  const addToSlip = () => {
    if (!isComplete || numericAmount < MIN_BET) return;
    setSlip((current) => [
      ...current,
      {
        key: `${Date.now()}-${current.length}`,
        betType,
        number,
        amount: numericAmount,
        multiplier,
      },
    ]);
    setNumber('');
  };

  const removeLine = (key) => {
    setSlip((current) => current.filter((line) => line.key !== key));
  };

  const handleSubmit = async () => {
    if (!slip.length) {
      await showError({ title: 'ยังไม่มีเลขในโพย', text: 'กดเพิ่มเลขเข้าโพยก่อนแทง' });
      return;
    }
    if (slipTotal > balance) {
      await showError({ title: 'ยอดไม่พอ', text: 'ยอดเงินไม่เพียงพอ กรุณาฝากเงิน' });
      return;
    }
    if (!bettingOpen) {
      await showError({ title: 'ปิดรับแทงแล้ว', text: 'งวดนี้ปิดรับแทงแล้ว รอออกรางวัล' });
      return;
    }

    const linesHtml = slip
      .map(
        (line) =>
          `<div class="flex justify-between gap-4 text-sm"><span>${LAO_BET_TYPE_LABELS[line.betType]} <strong>${line.number}</strong></span><span>${formatMoney(line.amount)}</span></div>`,
      )
      .join('');
    const confirmed = await confirmPurchase({
      title: 'ยืนยันแทงหวยลาว',
      html: `${linesHtml}<p class="mt-3 font-semibold">รวม ${formatMoney(slipTotal)} · ${slip.length} รายการ</p>`,
      confirmText: 'ยืนยันแทง',
    });
    if (!confirmed) return;

    const ok = await onPlaceBets({
      bets: slip.map((line) => ({
        betType: line.betType,
        number: line.number,
        amount: line.amount,
      })),
    });
    if (ok) setSlip([]);
  };

  return (
    <Card className="overflow-hidden border-2 border-brand-gold/40 shadow-xl">
      <CardHeader className="bg-gradient-to-r from-[#2b0a12] via-brand-red to-brand-red-dark">
        <CardTitle className="text-white">แทงหวยลาว</CardTitle>
        <p className="text-sm text-brand-gold-light">
          เลือกประเภท ใส่เลข แล้วเพิ่มเข้าโพยได้หลายรายการ
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {betTypes.map((item) => {
            const selected = item.id === betType;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleTabChange(item.id)}
                className={`rounded-2xl border-2 px-3 py-3 text-left transition ${
                  selected
                    ? 'border-brand-red bg-brand-red text-white shadow-md'
                    : 'border-red-100 bg-red-50/40 text-gray-700 hover:border-brand-gold'
                }`}
              >
                <p className="text-sm font-bold">{item.label}</p>
                <p className={`mt-1 text-xs ${selected ? 'text-brand-gold-light' : 'text-brand-gold'}`}>
                  {formatLaoPayout(item.multiplier)}
                </p>
              </button>
            );
          })}
        </div>

        <DigitBoxInput
          id="lao-bet"
          length={digitLength}
          value={number}
          onChange={setNumber}
          label={`${LAO_BET_TYPE_LABELS[betType] || ''} (${digitLength} หลัก)`}
        />
        <p className="text-center text-xs text-gray-500">
          {LAO_BET_TYPE_HINTS[betType]}
        </p>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            ยอดแทงรายการนี้ — ขั้นต่ำ ${MIN_BET}
          </label>
          <input
            type="number"
            min={MIN_BET}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 text-center text-xl font-semibold focus:border-brand-red focus:outline-none"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {QUICK_AMOUNTS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setAmount(String(value))}
                className={`rounded-lg border px-3 py-1 text-sm font-medium ${
                  Number(amount) === value
                    ? 'border-brand-red bg-red-50 text-brand-red'
                    : 'border-gray-200 text-gray-600 hover:border-brand-gold'
                }`}
              >
                ${value}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setNumber(randomLaoNumber(digitLength))}
              className="inline-flex items-center gap-1 rounded-lg border border-brand-gold/50 px-3 py-1 text-sm font-medium text-brand-gold hover:bg-brand-gold-light/30"
            >
              <Shuffle className="h-3.5 w-3.5" />
              สุ่มเลข
            </button>
          </div>
        </div>

        {isComplete ? (
          <div className="rounded-xl bg-gray-50 p-4 text-sm">
            <div className="flex items-end justify-end">
              <span className="self-end text-gray-700 mr-2 leading-none">รางวัลที่จะได้รับ</span>
              <span className="text-xl font-bold text-brand-gold leading-none self-end">
                {formatMoney(numericAmount * multiplier)}
              </span>
            </div>
          </div>
        ) : null}

        <Button
          variant="secondary"
          className="w-full"
          disabled={!isComplete || numericAmount < MIN_BET}
          onClick={addToSlip}
        >
          <Plus className="h-4 w-4" />
          เพิ่มเข้าโพย
        </Button>

        <div className="rounded-2xl border border-red-100 bg-red-50/30 p-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-bold text-brand-red">โพย ({slip.length})</p>
            {slip.length ? (
              <button
                type="button"
                className="text-xs text-gray-500 hover:text-brand-red"
                onClick={() => setSlip([])}
              >
                ล้างโพย
              </button>
            ) : null}
          </div>
          {slip.length === 0 ? (
            <p className="text-sm text-gray-500">ยังไม่มีเลขในโพย</p>
          ) : (
            <ul className="space-y-2">
              {slip.map((line) => (
                <li
                  key={line.key}
                  className="flex items-center justify-between gap-2 rounded-xl bg-white px-3 py-2"
                >
                  <div>
                    <p className="font-mono text-lg font-bold text-brand-red">
                      {line.number}
                    </p>
                    <p className="text-xs text-gray-500">
                      {LAO_BET_TYPE_LABELS[line.betType]} · {formatMoney(line.amount)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLine(line.key)}
                    className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-brand-red"
                    aria-label="ลบรายการ"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-right text-sm font-semibold">
            รวมโพย {formatMoney(slipTotal)}
          </p>
        </div>

        {!hasEnough && slipTotal >= MIN_BET ? (
          <p className="text-center text-sm text-brand-red">
            ยอดเงินไม่เพียงพอ กรุณาฝากที่หน้ากระเป๋าเงิน
          </p>
        ) : null}

        {!bettingOpen ? (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-center text-sm text-amber-800">
            ปิดรับแทงแล้ว รอออกรางวัลเวลา {config?.drawClock || '20:00'} น.
          </p>
        ) : null}

        <Button
          size="lg"
          className="w-full"
          disabled={isSubmitting || !slip.length || slipTotal > balance || !bettingOpen}
          onClick={handleSubmit}
        >
          <Send className="h-4 w-4" />
          {isSubmitting ? 'กำลังแทง...' : `แทงโพย ${formatMoney(slipTotal)}`}
        </Button>
      </CardContent>
    </Card>
  );
}
